import type { CountryProfile, Film } from "@/mocks/films";

type CountrySectionProps = {
  profile: CountryProfile;
  films: Film[];
  index: number;
};

const regionIcons: Record<string, string> = {
  france: "ri-camera-line",
  italy: "ri-leaf-line",
  spain: "ri-sun-line",
  germany: "ri-building-2-line",
  uk: "ri-cloud-line",
  other: "ri-earth-line",
};

export default function CountrySection({ profile, films, index }: CountrySectionProps) {
  const isOdd = index % 2 === 1;
  return (
    <section
      id={profile.key}
      className={`w-full py-14 md:py-20 border-t border-background-200 ${isOdd ? "bg-background-100" : "bg-background-50"}`}
    >
      <div className="w-full px-4 md:px-8 max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
                <i className={`${regionIcons[profile.key] ?? "ri-map-2-line"} text-lg`} aria-hidden="true"></i>
              </span>
              <span className="text-xs font-label tracking-[0.28em] uppercase text-foreground-500">{profile.en}</span>
            </div>
            <h2 className="mt-4 font-heading text-2xl md:text-3xl font-bold text-foreground-950">{profile.name}</h2>
            <p className="mt-3 text-sm md:text-base leading-relaxed text-foreground-700">{profile.lead}</p>
          </div>
          <div className="shrink-0 rounded-lg bg-background-50 border border-background-200 px-5 py-4 lg:max-w-[240px]">
            <p className="text-xs text-foreground-500">本区作品</p>
            <p className="mt-1 font-heading text-2xl font-bold text-primary-600">
              {films.length}
              <span className="ml-1 text-sm font-normal text-foreground-500">部</span>
            </p>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          {profile.traits.map((t) => (
            <span
              key={t}
              className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-900 text-xs md:text-sm"
            >
              {t}
            </span>
          ))}
        </div>

        <p className="mt-6 pl-4 border-l-2 border-accent-400 text-sm md:text-base italic text-foreground-700">
          {profile.focus}
        </p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {films.map((film) => (
            <article
              key={film.id}
              className="flex flex-col p-5 rounded-lg bg-background-50 border border-background-200 hover:border-primary-300 transition-colors"
            >
              <div className="flex items-center justify-between">
                <span className="px-2.5 py-1 rounded-md bg-primary-100 text-primary-700 text-xs">{film.countryName}</span>
                <span className="text-xs text-foreground-500 font-label">{film.year}</span>
              </div>

              <h3 className="mt-3.5 font-heading text-lg font-bold text-foreground-950 leading-snug">{film.title}</h3>
              <p className="mt-1 text-xs text-foreground-500 italic break-words">{film.original}</p>

              <p className="mt-3 flex items-center gap-1.5 text-xs text-foreground-600">
                <i className="ri-award-line text-accent-600" aria-hidden="true"></i>
                导演 · {film.director}
              </p>

              <p className="mt-3 text-sm leading-relaxed text-foreground-700 flex-1">{film.note}</p>

              <div className="mt-4 pt-4 border-t border-background-200 flex flex-wrap gap-1.5">
                {film.themes.map((t) => (
                  <span key={t} className="px-2 py-0.5 rounded bg-accent-100 text-accent-800 text-[11px]">
                    {t}
                  </span>
                ))}
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}